import { useQuery } from '@apollo/client';
import { 
  GET_CREATIVE_PROJECT, 
  CREATIVE_PROJECTS_BY_CREATIVE_ID,
  CREATIVE_PROJECTS_BY_STUDIO_ID,
  LIST_CREATIVE_PROJECTS 
} from '../../graphql/queries/creativeQueries';
import { useMemo } from 'react';
import { getCloudFrontImageUrl } from '../../util/cloudFrontUtils';
import awsConfig from '../../config/aws-config';

/**
 * Process project images (handles both string URLs and ProjectImage objects)
 */
const processProjectImages = (images) => {
  if (!images || !Array.isArray(images)) return [];
  
  return images.map(img => {
    if (typeof img === 'string') {
      // Simple string URL
      try {
        return {
          url: img,
          processedUrl: getCloudFrontImageUrl(img, {
            width: 1200,
            height: 800,
            fit: 'cover'
          })
        };
      } catch (error) {
        return { url: img, processedUrl: img };
      }
    } else if (img?.url) {
      // ProjectImage object with url, caption, thumbnailUrl, order
      const processedImg = { ...img };
      try {
        processedImg.processedUrl = getCloudFrontImageUrl(img.url, {
          width: 1200,
          height: 800,
          fit: 'cover'
        });
        if (img.thumbnailUrl) {
          processedImg.processedThumbnailUrl = getCloudFrontImageUrl(img.thumbnailUrl, {
            width: 400,
            height: 300,
            fit: 'cover'
          });
        }
      } catch (error) {
        processedImg.processedUrl = img.url;
      }
      return processedImg;
    }
    return img;
  });
};

/**
 * Hook to fetch a single Creative Project by ID
 * @param {string} projectId - The Project ID
 * @returns {object} - { project, loading, error }
 */
export const useCreativeProject = (projectId) => {
  const { data, loading, error } = useQuery(GET_CREATIVE_PROJECT, {
    variables: { id: projectId },
    skip: !projectId,
    fetchPolicy: 'cache-and-network',
  });

  const processedProject = useMemo(() => {
    if (!data?.getCreativeProject) return null;

    const project = { ...data.getCreativeProject };

    // Process images
    project.processedImages = processProjectImages(project.images);

    // Process audio files (for MUSIC type projects)
    if (project.audios && Array.isArray(project.audios)) {
      project.audios = project.audios.map(audio => {
        const processedAudio = { ...audio };
        
        // Store the clean key for audio URL resolution
        // Components should use getUrl from AWS Amplify to resolve the URL asynchronously
        // Note: We don't generate URLs here because getUrl is async and useMemo is sync
        if (audio.url) {
          // The key from uploadData with accessLevel: 'public' already includes 'public/' prefix
          // So we should use it as-is, but normalize it to ensure it has the prefix
          // Remove any duplicate 'public/' prefixes first
          let cleanKey = audio.url;
          while (cleanKey.startsWith('public/public/')) {
            cleanKey = cleanKey.replace('public/public/', 'public/');
          }
          // Ensure it has the prefix (in case it doesn't for some reason)
          if (!cleanKey.startsWith('public/')) {
            cleanKey = `public/${cleanKey}`;
          }
          processedAudio.audioKey = cleanKey;
          // Store original URL as fallback
          processedAudio.originalUrl = audio.url;
        }
        
        // Process cover image for audio
        if (audio.coverImage) {
          try {
            processedAudio.processedCoverImage = getCloudFrontImageUrl(audio.coverImage, {
              width: 600,
              height: 600,
              fit: 'cover'
            });
          } catch (error) {
            try {
              const cleanKey = audio.coverImage.startsWith('public/') 
                ? audio.coverImage 
                : `public/${audio.coverImage}`;
              processedAudio.processedCoverImage = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
            } catch (fallbackError) {
              processedAudio.processedCoverImage = audio.coverImage;
            }
          }
        }
        
        return processedAudio;
      });
    }

    // Process creative profile picture
    if (project.creative?.profilePicture) {
      let processedProfilePicture = "";
      try {
        processedProfilePicture = getCloudFrontImageUrl(project.creative.profilePicture, {
          width: 200,
          height: 200,
          fit: 'cover'
        });
      } catch (error) {
        processedProfilePicture = "";
      }
      project.creative = {
        ...project.creative,
        processedProfilePicture
      };
    }

    // Process studio images
    if (project.studio?.images && Array.isArray(project.studio.images)) {
      const processedStudioImages = project.studio.images.map(imageKey => {
        if (!imageKey) return '';
        try {
          return getCloudFrontImageUrl(imageKey, {
            width: 600,
            height: 450,
            fit: 'cover'
          });
        } catch (error) {
          return '';
        }
      });
      project.studio = {
        ...project.studio,
        processedImages: processedStudioImages
      };
    }

    return project;
  }, [data]);

  return {
    project: processedProject,
    loading,
    error
  };
};

/**
 * Hook to fetch all Projects for a specific Creative
 * @param {string} creativeId - The Creative ID
 * @param {object} options - Query options (filter, sortDirection, limit)
 * @returns {object} - { projects, loading, error, hasProjects }
 */
export const useCreativeProjectsByCreativeId = (creativeId, options = {}) => {
  const { data, loading, error } = useQuery(CREATIVE_PROJECTS_BY_CREATIVE_ID, {
    variables: {
      creativeID: creativeId,
      filter: options.filter,
      sortDirection: options.sortDirection || 'DESC',
      limit: options.limit || 100,
      nextToken: options.nextToken
    },
    skip: !creativeId,
    fetchPolicy: 'cache-and-network',
  });

  const processedData = useMemo(() => {
    if (!data?.creativeProjectsByCreativeID?.items) return null;

    const projects = data.creativeProjectsByCreativeID.items.map(project => {
      const processedProject = { ...project };
      processedProject.processedImages = processProjectImages(project.images);

      // Process studio images
      if (processedProject.studio?.images && Array.isArray(processedProject.studio.images)) {
        processedProject.studio.processedImages = processedProject.studio.images.map(imageKey => {
          if (!imageKey) return '';
          try {
            return getCloudFrontImageUrl(imageKey, {
              width: 600,
              height: 450,
              fit: 'cover'
            });
          } catch (error) {
            return '';
          }
        });
      }

      return processedProject;
    });

    return {
      projects: projects,
      nextToken: data.creativeProjectsByCreativeID.nextToken,
      hasProjects: projects.length > 0
    };
  }, [data]);

  return {
    projectsData: processedData,
    loading,
    error
  };
};

/**
 * Hook to fetch all Projects for a specific Studio
 * @param {string} studioId - The Studio ID
 * @param {object} options - Query options (filter, sortDirection, limit)
 * @returns {object} - { projects, loading, error }
 */
export const useCreativeProjectsByStudioId = (studioId, options = {}) => {
  const { data, loading, error } = useQuery(CREATIVE_PROJECTS_BY_STUDIO_ID, {
    variables: {
      studioID: studioId,
      filter: options.filter,
      sortDirection: options.sortDirection || 'DESC',
      limit: options.limit || 100,
      nextToken: options.nextToken
    },
    skip: !studioId,
    fetchPolicy: 'cache-and-network',
  });

  const processedData = useMemo(() => {
    if (!data?.creativeProjectsByStudioID?.items) return null;

    const projects = data.creativeProjectsByStudioID.items.map(project => {
      const processedProject = { ...project };
      processedProject.processedImages = processProjectImages(project.images);

      // Process creative profile picture
      if (processedProject.creative?.profilePicture) {
        let processedProfilePicture = "";
        try {
          processedProfilePicture = getCloudFrontImageUrl(processedProject.creative.profilePicture, {
            width: 200,
            height: 200,
            fit: 'cover'
          });
        } catch (error) {
          processedProfilePicture = "";
        }
        processedProject.creative = {
          ...processedProject.creative,
          processedProfilePicture
        };
      }

      return processedProject;
    });

    return {
      projects: projects,
      nextToken: data.creativeProjectsByStudioID.nextToken
    };
  }, [data]);

  return {
    projects: processedData?.projects || [],
    nextToken: processedData?.nextToken,
    loading,
    error
  };
};

/**
 * Hook to list all Creative Projects with optional filters
 * @param {object} options - Query options (filter, limit, nextToken)
 * @returns {object} - { projects, loading, error }
 */
export const useListCreativeProjects = (options = {}) => {
  const { data, loading, error } = useQuery(LIST_CREATIVE_PROJECTS, {
    variables: {
      filter: options.filter,
      limit: options.limit || 100,
      nextToken: options.nextToken
    },
    fetchPolicy: 'cache-and-network',
  });

  const processedData = useMemo(() => {
    if (!data?.listCreativeProjects?.items) return null;

    const projects = data.listCreativeProjects.items.map(project => {
      const processedProject = { ...project };
      processedProject.processedImages = processProjectImages(project.images);

      // Process creative profile picture
      if (processedProject.creative?.profilePicture) {
        let processedProfilePicture = "";
        try {
          processedProfilePicture = getCloudFrontImageUrl(processedProject.creative.profilePicture, {
            width: 200,
            height: 200,
            fit: 'cover'
          });
        } catch (error) {
          processedProfilePicture = "";
        }
        processedProject.creative = {
          ...processedProject.creative,
          processedProfilePicture
        };
      }

      // Process studio images
      if (processedProject.studio?.images && Array.isArray(processedProject.studio.images)) {
        processedProject.studio.processedImages = processedProject.studio.images.map(imageKey => {
          if (!imageKey) return '';
          try {
            return getCloudFrontImageUrl(imageKey, {
              width: 600,
              height: 450,
              fit: 'cover'
            });
          } catch (error) {
            return '';
          }
        });
      }

      return processedProject;
    });

    return {
      projects: projects,
      nextToken: data.listCreativeProjects.nextToken
    };
  }, [data]);

  return {
    projects: processedData?.projects || [],
    nextToken: processedData?.nextToken,
    loading,
    error
  };
};

